<template>
  <div class="gv-preview">
    <div class="gv-preview__head">
      <h2 class="gv-preview__heading">Preview</h2>
      <div class="gv-preview__modes" role="tablist" aria-label="Preview mode">
        <button
          v-for="option in modes"
          :key="option.value"
          type="button"
          role="tab"
          class="gv-preview__mode"
          :class="{ 'is-active': mode === option.value }"
          :aria-selected="mode === option.value ? 'true' : 'false'"
          :data-mode="option.value"
          @click="$emit('update:mode', option.value)"
        >
          <gs-icon :name="option.icon" size="14" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <p
      v-if="youtubeProcessing"
      class="gv-preview__notice"
    >
      <gs-icon name="eye" size="14" />
      <span>
        {{ $t('frontend_groovevideo_index_youtube_processing') }}
        {{ $t('frontend_groovevideo_index_youtube_processing_takes_time') }}
      </span>
    </p>

    <div class="gv-preview__stage">
      <!-- Still encoding: the wizard's spinner panel, kept verbatim in wording. -->
      <editor-canvas-preview v-if="mode === 'editor'" :model="model" />
      <div v-else-if="status === false" class="gv-preview__encoding">
        <half-circle-spinner :animation-duration="1000" :size="60" color="dark-pink" />
        <p class="gv-preview__encoding-copy">
          {{
            model.video_source_type === 2
              ? $t('frontend_groovevideo_addnew_video_uploading_to_youtube')
              : $t('frontend_groovevideo_addnew_video_encoding_video')
          }}
        </p>
      </div>


      <div v-else-if="isVideoSrc === true" :style="playerColor">
        <video-player
          id="vid2"
          :options="model"
          :key="videoPlayerComponentKey"
          class="video-js vjs-big-play-centered"
          :class="vjsClass"
          :overlay_text="overlayText"
          data-setup='{ "playbackRates": [0.5, 1, 1.25, 1.5, 1.75, 2] }'
        ></video-player>
        <button id="load" type="button"></button>
        <div id="console" class="hidden"></div>
      </div>

      <p v-else class="gv-preview__unavailable">
        {{ $t('frontend_groovevideo_addnew_video_encoding_upload') }}
      </p>
    </div>

    <p v-if="encoding" class="gv-preview__encoding-hint">
      {{ $t('frontend_groovevideo_addnew_video_encoding_video') }}
    </p>
  </div>
</template>

<script>
import VideoPlayer from '../VideoPlayer';
import EditorCanvasPreview from './EditorCanvasPreview.vue';

export default {
  name: 'EditorPreview',

  components: {
    VideoPlayer,
    EditorCanvasPreview,
  },

  props: {
    model: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'editor',
    },
    status: {
      type: Boolean,
      default: false,
    },
    isVideoSrc: {
      type: Boolean,
      default: false,
    },
    encoding: Boolean,
  },

  data() {
    return {
      modes: [
        { value: 'editor', label: 'Editor', icon: 'settings' },
        { value: 'audience', label: 'Audience', icon: 'play' },
      ],
    };
  },

  computed: {
    // Bumped by the `videoComponentKey` store action so the real player
    // remounts whenever a setting that videojs reads at build time changes.
    videoPlayerComponentKey() {
      return this.$store.state.video.videoPlayerComponentKey;
    },

    // Ported from the wizard.
    playerColor() {
      const settings = this.model.settings || {};
      if (
        typeof settings.color !== 'undefined' &&
        typeof settings.color.primary !== 'undefined' &&
        typeof settings.color.secondary !== 'undefined'
      ) {
        return {
          '--player-primary-color': settings.color.primary,
          '--player-secondary-color': settings.color.secondary,
        };
      }
      return {
        '--player-primary-color': 'rgba(253, 100, 129, 1)',
        '--player-secondary-color': 'rgba(255,255,255, 1)',
      };
    },

    // Ported from the wizard.
    vjsClass() {
      const settings = this.model.settings || {};
      if (settings.theme == null || settings.theme == ' ') {
        return 'vjs-theme-default';
      }
      return settings.theme;
    },

    overlayText() {
      const settings = this.model.settings || {};
      return (settings.overlay && settings.overlay.txt) || '';
    },

    // Ported from the wizard's "YouTube is still processing" condition.
    youtubeProcessing() {
      return (
        this.model.video_source_type === 2 &&
        Boolean(this.model.is_uploading === 1 || this.model.temp_video_source)
      );
    },
  },
};
</script>

<style scoped>
.gv-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gv-preview__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.gv-preview__heading {
  flex: 1 1 auto;
  margin: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 700;
}

.gv-preview__modes {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-md);
}

.gv-preview__mode {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gv-preview__mode:hover {
  color: var(--gs-foreground);
}

.gv-preview__mode:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gv-preview__mode.is-active {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  box-shadow: var(--gs-shadow-sm);
}

.gv-preview__notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gv-preview__stage {
  overflow: hidden;
  background: var(--gs-foreground);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gv-preview__encoding {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 260px;
  padding: 24px;
}

.gv-preview__encoding-copy {
  margin: 0;
  color: var(--gs-primary-foreground);
  font-size: 15px;
  font-weight: 600;
  text-align: center;
}

.gv-preview__unavailable {
  margin: 0;
  padding: 40px 24px;
  color: var(--gs-primary-foreground);
  font-size: 14px;
  text-align: center;
}

.gv-preview__encoding-hint {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gv-preview__stage ::v-deep .video-js {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.gv-preview__stage ::v-deep .videojs-event-tracking-player-dimensions {
  width: 0 !important;
  height: 0 !important;
}
</style>

<!--
  Unscoped on purpose: video.js renders its control bar, big play button and
  CTA overlays outside this component's scoped tree, and the wizard used to own
  these rules. They read the `--player-*` custom properties set by `playerColor`.
-->
<style>
.vjs-control-bar,
.cta-optin-form-submit-button.videoBtn,
.cta-optin-form-submit-button.videoBtn:hover,
.cta-optin-form-submit-button.videoBtn:active,
.cta-optin-form-submit-button.videoBtn:focus,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:hover,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:active {
  background-color: var(--player-primary-color) !important;
}

.optin-skip.cta-skip-button:hover,
.cta-skip-banner-button.banner-skip:hover {
  color: var(--player-primary-color) !important;
}

.video-js {
  color: var(--player-secondary-color) !important;
}

.vjs-big-play-button {
  border: 0 !important;
}

.vjs-big-play-button:active,
.vjs-big-play-button:focus {
  outline: none;
}
</style>
